import { memo } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, CalendarDays, ExternalLink } from "lucide-react";

function HeroApprovedBase({ firstName, approvedAt, hubUrl }) {
  return (
    <div data-testid="approved-hero">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-sm border border-[#4C7F3E]/40 bg-[#4C7F3E]/12"
      >
        <BadgeCheck className="w-4 h-4 text-[#4C7F3E]" strokeWidth={2.25} />
        <span
          className="text-[10px] tracking-[0.35em] uppercase text-[#4C7F3E]"
          style={{ fontFamily: "'Noir Pro', serif", fontWeight: 700 }}
        >
          Reelty Approved
        </span>
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 14, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
        className="font-display font-extrabold tracking-tight leading-[0.95] text-[4.25rem] sm:text-[5.25rem] md:text-[6.25rem] text-[#4C7F3E]"
        style={{ textShadow: "0 2px 16px rgba(76,127,62,0.15)" }}
        data-testid="approved-headline"
      >
        Approved.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.35 }}
        className="text-[#4D3F35] mt-5 max-w-xl leading-relaxed text-[15px]"
      >
        Congrats, {firstName} — you're officially on the Reelty roster.
        The Property Shoots Hub is open to you. The Reelty team will reach out with your first assignment.
      </motion.p>

      {/* Enter the Portal — gold CTA that opens the Shoots Hub in a new tab.
          Disabled state (with a "coming soon" hint) when REACT_APP_SHOOTS_HUB_URL
          hasn't been configured yet so the button is discoverable early. */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mt-6"
      >
        {hubUrl ? (
          <a
            href={hubUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enter-portal-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm tracking-[0.25em] uppercase transition-all hover:brightness-110 hover:shadow-[0_18px_44px_-14px_rgba(176, 141, 87,0.75)] focus-visible:ring-2 focus-visible:ring-[#4C7F3E]/60 focus-visible:outline-none"
            style={{
              backgroundImage: "linear-gradient(90deg, #B08D57 0%, #D4B584 50%, #B08D57 100%)",
              color: "#1A0308",
              fontFamily: "'Noir Pro', serif",
              fontWeight: 700,
              boxShadow: "0 12px 36px -12px rgba(176, 141, 87,0.55)",
            }}
          >
            Enter the Portal
            <ExternalLink className="w-4 h-4" strokeWidth={2.25} />
          </a>
        ) : (
          <div
            data-testid="enter-portal-btn-pending"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm tracking-[0.25em] uppercase cursor-not-allowed select-none"
            title="Shoots Hub URL not configured yet — set REACT_APP_SHOOTS_HUB_URL in frontend/.env"
            style={{
              backgroundImage: "linear-gradient(90deg, #B08D57 0%, #D4B584 50%, #B08D57 100%)",
              color: "#1A0308",
              fontFamily: "'Noir Pro', serif",
              fontWeight: 700,
              opacity: 0.55,
            }}
          >
            Enter the Portal
            <span
              className="ml-1 text-[9px] tracking-[0.3em] px-1.5 py-0.5 rounded-sm bg-[#1A0308]/15"
              style={{ fontFamily: "'Noir Pro', serif" }}
            >
              COMING SOON
            </span>
          </div>
        )}
      </motion.div>

      {approvedAt && (
        <div
          className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#78584B] flex items-center gap-2"
          style={{ fontFamily: "'Noir Pro', serif" }}
        >
          <CalendarDays className="w-3 h-3" strokeWidth={2} />
          Approved{" "}
          {new Date(approvedAt).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      )}
    </div>
  );
}

export const HeroApproved = memo(HeroApprovedBase);
